import { z } from "zod";
import {
  EntitlementKind,
  EntitlementStatus,
  HttpsUrl,
  IsoDateTime,
  LEDGER_CURRENCY,
  NonNegativeMoney,
  ProvisioningStatus,
  ResourceState,
  Uuid,
} from "./common.ts";
import { Order, OrderProduct } from "./orders.ts";
import { actionsOf } from "./permissions.ts";

/*
 * Entitlements (07 §2.2, UC-06, UC-07, UC-10, 06 `entitlement`, `provisioned_resource`).
 * Access hangs on the entitlement, never on the order (Grundsatz 1). The AI key behind it
 * stays internal in the alpha release and is not part of any customer response (R-13).
 */

/** The result of a provisioning: where the customer finds the product (ADR-031). */
export const ProvisionedResource = z.object({
  /** in the alpha release `ai_cockpit` */
  kind: z.string().min(1),
  /** identifier in the target system */
  externalRef: z.string().nullable(),
  /** address for the customer, https only: the portal links it (M15) */
  url: HttpsUrl.nullable(),
  state: ResourceState,
});
export type ProvisionedResource = z.infer<typeof ProvisionedResource>;

export const Entitlement = z.object({
  id: Uuid,
  customerId: Uuid,
  product: OrderProduct,
  orderId: Uuid.nullable(),
  kind: EntitlementKind,
  status: EntitlementStatus,
  /**
   * set by the customer, proposed by the product (R-04); fixed by the product for a demo. Null for
   * an actor without `usage.view`, the user, who reads no amounts (ADR-158 point F12.3-2, AP-B14-12)
   */
  monthlyLimit: NonNegativeMoney.nullable(),
  /**
   * The highest monthly limit this entitlement may get now (ADR-158 point F12.2-4, AP-B14-09): the
   * credit limit minus the monthly limits of the other living entitlements, as the check of
   * `limit.set` counts them (R-04, ADR-087). Null without a credit limit (registered or suspended,
   * GC4), without AI access (no limit, ADR-144) and for an actor without `usage.view` (AP-B14-12).
   * The limit dialog shows it as "Hier möglich"; the check at submitting stays what counts. For a
   * demo and an ended entitlement it is only the value by R-04: `limit.set` refuses both
   * (`demo_limit_fixed`, `entitlement_not_live`), so the dialog does not open for them (BL2).
   */
  maxMonthlyLimit: NonNegativeMoney.nullable(),
  /**
   * runs over the platform's AI keys (ADR-144): the order's AI access, without an order the
   * product's. Without it there is no key and no monthly limit (zero); the portal reads this, not
   * the reason of `limit.set`, which names a suspended customer first (C1)
   */
  aiAccess: z.boolean(),
  /** running period of a demo or subscription (F-44) */
  period: z
    .object({
      start: IsoDateTime,
      end: IsoDateTime.nullable(),
    })
    .nullable(),
  provisioning: z.object({
    status: ProvisioningStatus,
    error: z.string().nullable(),
  }),
  resource: ProvisionedResource.nullable(),
  /** demo converted to standard, same row (ADR-030) */
  convertedAt: IsoDateTime.nullable(),
  endedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  /**
   * What the actor may do with this entitlement (ADR-075). Reasons that occur:
   * `role_required`, `customer_not_approved`, `customer_suspended`, `entitlement_not_live`,
   * `demo_limit_fixed`. `entitlement.cancel` decides whether the portal shows how to cancel: the
   * administrator, a living entitlement, a demo too, whatever the customer's state (UC-16); the
   * cancellation itself goes through an inquiry in the alpha release, there is no route for it.
   */
  actions: actionsOf("limit.set", "entitlement.cancel"),
});
export type Entitlement = z.infer<typeof Entitlement>;

/** `GET /api/entitlements`, all entitlements of the customer including ended ones. Not paged. */
export const EntitlementListResponse = z.object({
  items: z.array(Entitlement),
});
export type EntitlementListResponse = z.infer<typeof EntitlementListResponse>;

/**
 * `PUT /api/entitlements/:id/monthly-limit` (UC-10). Only in the ledger currency, USD
 * (ADR-058). Rejected with `limit_exceeded` and reason `credit_limit_exceeded` when the sum of
 * all living limits would pass the credit limit; the message names the credit limit and the
 * sum of the other limits, `details` carries both as `CreditLimitExceededDetails`. Rejected with
 * `forbidden` and `role_required`, `customer_not_approved` or `customer_suspended` (a suspended
 * customer reads that it is suspended, not that it waits for approval) for the actor, with `conflict` and
 * `entitlement_not_live` or `demo_limit_fixed` (a demo's limit cannot be changed) for the
 * entitlement.
 */
export const MonthlyLimitRequest = z.object({
  monthlyLimit: NonNegativeMoney.refine((money) => money.currency === LEDGER_CURRENCY, {
    message: `the monthly limit is in ${LEDGER_CURRENCY} (ADR-058)`,
    path: ["currency"],
  }),
});
export type MonthlyLimitRequest = z.infer<typeof MonthlyLimitRequest>;

/** `details` of the `limit_exceeded` rejection (UC-10 acceptance criterion): what the message names. */
export const CreditLimitExceededDetails = z.object({
  creditLimit: NonNegativeMoney,
  /** sum of the monthly limits of the customer's other living entitlements */
  otherLimits: NonNegativeMoney,
});
export type CreditLimitExceededDetails = z.infer<typeof CreditLimitExceededDetails>;

/** The new limit and the remaining headroom under the credit limit (UC-10 step 4). */
export const MonthlyLimitResponse = z.object({
  entitlement: Entitlement,
  creditLimit: NonNegativeMoney,
  /** sum of the monthly limits of all living entitlements, including this one */
  limitSum: NonNegativeMoney,
  /** credit limit minus limit sum */
  remaining: NonNegativeMoney,
});
export type MonthlyLimitResponse = z.infer<typeof MonthlyLimitResponse>;

/**
 * `POST /api/entitlements/demo` (UC-06), answered with `201`. Creates an order of kind `demo`,
 * no price; the entitlement itself comes with the provisioning by staff (UC-04, UC-08). A
 * product the catalogue does not show is `404`. Rejected with `forbidden` and `role_required` or
 * `customer_suspended` for the actor, with `conflict` and `inquiry_only` (ADR-112, first),
 * `demo_not_offered`, `entitlement_exists`, `demo_already_used` (R-08) or `order_open` (ADR-029)
 * for the product.
 */
export const DemoRequest = z.object({
  productId: Uuid,
});
export type DemoRequest = z.infer<typeof DemoRequest>;

export const DemoResponse = Order;
export type DemoResponse = z.infer<typeof DemoResponse>;
